import React, { useEffect, useRef, useState } from 'react';
import api from '../api';

/**
 * CertificatePreview
 *
 * Fetches the certificate PDF from:
 *   GET /api/audit/certificates/{id}/preview
 * and renders it in an <iframe> so the user can view and download/print it
 * directly from the browser's built-in PDF viewer.
 *
 * Props:
 *   - data   : certificate row object — must contain at least { id }
 *   - onClose: called when the user clicks Close
 *   - onIssue: called when the user clicks Issue Certificate (optional)
 */
const CertificatePreview = ({ data = {}, onClose, onIssue }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const objectUrlRef = useRef(null);

  const certId = data.id;

  useEffect(() => {
    if (!certId) {
      setError('No certificate ID provided.');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchPdf = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get(
          `/audit/certificates/${certId}/preview`,
          { responseType: 'blob' }
        );

        if (cancelled) return;

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setPdfUrl(url);
      } catch (err) {
        if (!cancelled) {
          const msg = err?.response?.status === 404
            ? 'Certificate not found.'
            : 'Failed to load certificate PDF. Please try again.';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPdf();

    return () => {
      cancelled = true;
      // Revoke the object URL to free memory when the component unmounts
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [certId]);

  const pageStyle = {
    position: 'fixed',
    inset: 0,
    zIndex: 1050,
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '20px 16px',
    overflowY: 'auto',
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: 10,
    width: '100%',
    maxWidth: '900px',
    boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const toolbarStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc',
    gap: 10,
    flexWrap: 'wrap',
  };

  const titleStyle = {
    fontWeight: 700,
    fontSize: 15,
    color: '#0d3d42',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const btnBase = {
    border: 'none',
    borderRadius: 6,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  };

  return (
    <div style={pageStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={cardStyle}>
        {/* Toolbar */}
        <div style={toolbarStyle}>
          <span style={titleStyle}>
            Certificate Preview
            {data.certificateNumber ? ` — ${data.certificateNumber}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {pdfUrl && (
              <a
                href={pdfUrl}
                download={`certificate-${certId}.pdf`}
                style={{
                  ...btnBase,
                  background: '#2a8a93',
                  color: '#fff',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                Download PDF
              </a>
            )}
            {onIssue && (
              <button
                type="button"
                onClick={() => onIssue?.(data)}
                style={{ ...btnBase, background: '#0f766e', color: '#fff' }}
              >
                Issue Certificate
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                ...btnBase,
                background: 'transparent',
                border: '1px solid #cbd5e1',
                color: '#475569',
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* PDF viewer area */}
        <div style={{ position: 'relative', minHeight: 540 }}>
          {loading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  border: '3px solid #e2e8f0',
                  borderTopColor: '#2a8a93',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              <span style={{ fontSize: 13 }}>Loading certificate…</span>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {error && !loading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#b91c1c',
                gap: 8,
                padding: 24,
                textAlign: 'center',
              }}
            >
              <span style={{ fontSize: 32 }}>⚠</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{error}</span>
            </div>
          )}

          {pdfUrl && !loading && (
            <iframe
              src={pdfUrl}
              title="Certificate PDF"
              style={{
                width: '100%',
                height: '80vh',
                minHeight: 540,
                border: 'none',
                display: 'block',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificatePreview;
